import React, { useEffect, useState, useCallback } from 'react';
import { getSinglesMatches, getDoublesMatches, /* confirmMatch, */ rejectMatch, getPools } from '../services/api';
import ProfileOverlay from './ProfileOverlay';
import AdBlock from "./AdBlock";
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from './ui/LoadingSpinner';
import Select from 'react-select';
import { FaTimes, FaArrowRight, FaRegCalendarAlt } from 'react-icons/fa';
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { format } from 'date-fns';
import { useDashboard } from '../context/DashboardContext';

// Helper to get cache object from localStorage
function getCache(key) {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : null;
    const data = parsed && typeof parsed === 'object' && 'data' in parsed ? parsed.data : parsed;
    if (!data) return { data: null, lastFetched: null };
    try {
        if (data && typeof data === 'object' && 'data' in data && 'lastFetched' in data) {
            return data;
        } else {
            // Backward compatibility: treat as data only
            return { data: data, lastFetched: null };
        }
    } catch {
        return { data: null, lastFetched: null };
    }
}

// Helper function to format pool location string (format: "country#admin1#admin2#locality#sublocality")
const formatPoolLocationString = (location) => {
    // Handle both string and object formats
    let locationString = '';
    if (typeof location === 'string') {
        locationString = location;
    } else if (location && typeof location === 'object') {
        // If it's an object, format it similar to user locations
        const parts = [];
        if (location.sublocality) parts.push(location.sublocality);
        if (location.locality) parts.push(location.locality);
        if (location.administrative_area_level_1) parts.push(location.administrative_area_level_1);
        if (location.country) parts.push(location.country);
        return parts.length > 0 ? parts.join(', ') : '';
    }
    
    if (!locationString) return '';
    
    const parts = locationString.split('#').filter(Boolean);
    if (parts.length === 0) return '';
    
    // Format similar to user location: sublocality, locality, admin1, country (omitting admin2)
    const [country, admin1, admin2, locality, sublocality] = parts;
    const displayParts = [];
    if (sublocality) displayParts.push(sublocality);
    if (locality) displayParts.push(locality);
    if (admin1) displayParts.push(admin1);
    if (country) displayParts.push(country);
    
    return displayParts.join(', ');
};

// Pool Selection Modal Component
const PoolSelectionModal = ({ isOpen, onClose, pools, onSelect }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold">Select Pool</h2>
                    <button 
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        <FaTimes />
                    </button>
                </div>
                
                <div className="mb-4">
                    <Select
                        options={pools.map(pool => ({
                            value: pool.name,
                            label: (
                                <div className="flex items-center">
                                    {pool.picture ? (
                                        <img 
                                            src={pool.picture} 
                                            alt="" 
                                            className="w-8 h-8 rounded-full mr-2 object-cover"
                                        />
                                    ) : (
                                        <div className="w-8 h-8 rounded-full bg-gray-200 mr-2 flex items-center justify-center">
                                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                            </svg>
                                        </div>
                                    )}
                                    <div className="flex flex-col">
                                        <span className="font-medium">{pool.name}</span>
                                        {pool.location && (
                                            <span className="text-sm text-gray-500">
                                                {formatPoolLocationString(pool.location)}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )
                        }))}
                        onChange={(selected) => {
                            onSelect(selected.value);
                            onClose();
                        }}
                        placeholder="Search pools..."
                        className="w-full"
                        classNamePrefix="select"
                        isClearable
                    />
                </div>
            </div>
        </div>
    );
};

const Matches = ({ userId = null, title = 'All matches for location', cacheKeyPrefix, initialLocation, initialTab = 'singles' }) => {
    const [matches, setMatches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeMatchTab, setActiveMatchTab] = useState(initialTab);
    const [selectedPlayer, setSelectedPlayer] = useState(null);
    const [localUserId, setLocalUserId] = useState(userId);
    const { addToast } = useToast();
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const { updateCache, updateRankingsCache, removeMatchFromRecentCache, removeMatchFromUserCache, removeMatchFromAllCache, markMatchConfirmedInCaches } = useDashboard();

    // Pagination states
    const [hasMore, setHasMore] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    // Filter states
    const [genderFilter, setGenderFilter] = useState('');
    const [poolFilter, setPoolFilter] = useState('');
    const [ageFilter, setAgeFilter] = useState('');
    const [pools, setPools] = useState([]);
    const [showPoolModal, setShowPoolModal] = useState(false);
    const [filteredMatches, setFilteredMatches] = useState([]);
    const [matchTypeFilter, setMatchTypeFilter] = useState('');

    // Location filter states
    const [locations, setLocations] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState('');
    const [selectedRegion, setSelectedRegion] = useState('');
    const [selectedSubRegion, setSelectedSubRegion] = useState('');
    const [selectedLocality, setSelectedLocality] = useState('');

    // Date range picker state
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [dateRange, setDateRange] = useState([
      {
        startDate: null,
        endDate: null,
        key: 'selection'
      }
    ]);
    const [appliedDateRange, setAppliedDateRange] = useState(null);

    /**
     * activeLocation initialization priority:
     * 1. Use cache for this context if available
     * 2. Use initialLocation prop if provided
     * 3. Fallback to logged-in user's location
     */
    const [activeLocation, setActiveLocation] = useState(() => {
        const cacheSingles = getCache(`${cacheKeyPrefix}SinglesMatches`).data;
        const cacheDoubles = getCache(`${cacheKeyPrefix}DoublesMatches`).data;
        const cacheLoc = cacheSingles?.locationString || cacheDoubles?.locationString;
        if (cacheLoc) return cacheLoc;
        if (typeof initialLocation === 'string' && initialLocation.length > 0) return initialLocation;
        const storedProfile = JSON.parse(localStorage.getItem('userProfile'))?.data;
        return `${storedProfile?.location?.country}#${storedProfile?.location?.administrative_area_level_1}`;
    });

    // Load user profile and userLocation once
    const storedProfile = JSON.parse(localStorage.getItem('userProfile'))?.data;
    const isAdmin = storedProfile?.userType === 'admin';

    // Helper to parse a location string into dropdown values
    function parseLocationString(locStr) {
        if (!locStr) return {};
        const [country, region, subRegion, locality] = locStr.split('#');
        return { country, region, subRegion, locality };
    }

    // Determine cache key prefix
    let prefix = cacheKeyPrefix;
    if (!prefix) {
        if (userId === null) prefix = 'all';
        else prefix = 'user';
    }

    // 1. Dropdown initialization effect (runs only once)
    useEffect(() => {
        const cachedLocations = JSON.parse(localStorage.getItem('locations'))?.data;
        if (cachedLocations) {
            setLocations(cachedLocations);
        }
        const cacheSingles = getCache(`${prefix}SinglesMatches`).data;
        const cacheDoubles = getCache(`${prefix}DoublesMatches`).data;
        const cacheObj = cacheSingles || cacheDoubles;
        const cacheLoc = cacheObj?.locationString;
        // If no cache, use initialLocation to set dropdowns
        if (!cacheLoc && initialLocation && typeof initialLocation === 'string' && initialLocation.length > 0) {
            const [country, region, subRegion, locality] = initialLocation.split('#');
            if (!selectedCountry) setSelectedCountry(country || '');
            if (!selectedRegion) setSelectedRegion(region || '');
            if (!selectedSubRegion) setSelectedSubRegion(subRegion || '');
            if (!selectedLocality) setSelectedLocality(locality || '');
        } else if (cacheLoc) {
            // Handle case where cacheLoc might be an array (Global selection)
            if (Array.isArray(cacheLoc)) {
                if (!selectedCountry) setSelectedCountry('__GLOBAL__');
                if (!activeLocation) setActiveLocation(cacheLoc);
            } else {
                // Handle string location as before
                const { country, region, subRegion, locality } = parseLocationString(cacheLoc);
                if (!selectedCountry) setSelectedCountry(country || '');
                if (!selectedRegion) setSelectedRegion(region || '');
                if (!selectedSubRegion) setSelectedSubRegion(subRegion || '');
                if (!selectedLocality) setSelectedLocality(locality || '');
            }
        } else if (storedProfile?.location) {
            if (!selectedCountry) setSelectedCountry(storedProfile.location.country || '');
            if (!selectedRegion) setSelectedRegion(storedProfile.location.administrative_area_level_1 || '');
        }
        // Restore date range from cache if present
        if (cacheObj?.startTimestamp && cacheObj?.endTimestamp) {
            setAppliedDateRange({
                startDate: new Date(cacheObj.startTimestamp),
                endDate: new Date(cacheObj.endTimestamp)
            });
            setDateRange([{ startDate: new Date(cacheObj.startTimestamp), endDate: new Date(cacheObj.endTimestamp), key: 'selection' }]);
        }
        setLocalUserId(storedProfile?.userId);
    }, [userId, prefix, initialLocation]);

    // 2. Fetch matches effect (depends on activeLocation)
    useEffect(() => {
        setLoading(true);
        const cacheKey = activeMatchTab === 'singles' 
            ? `${prefix}SinglesMatches` 
            : `${prefix}DoublesMatches`;
        const cached = getCache(cacheKey).data;
        const cachedMatches = cached?.matches || [];
        const cachedLastKey = cached?.lastEvaluatedKey || null;
        const cachedLocation = cached?.locationString;
        if (cachedMatches.length > 0 && cachedLocation === activeLocation) {
            setMatches(cachedMatches);
            setFilteredMatches(cachedMatches);
            setHasMore(!!cachedLastKey && Object.values(cachedLastKey).some(v => v));
            setLoading(false);
            getPools(userId).then(setPools);
            return;
        }
        // Otherwise, fetch from API
        const fetchData = async () => {
            try {
                const fetchFunction = activeMatchTab === 'singles' ? getSinglesMatches : getDoublesMatches;
                // Use appliedDateRange for timestamps if set
                let startTimestamp = null, endTimestamp = null;
                if (appliedDateRange && appliedDateRange.startDate && appliedDateRange.endDate) {
                    startTimestamp = new Date(appliedDateRange.startDate).setHours(0,0,0,0);
                    endTimestamp = new Date(appliedDateRange.endDate).setHours(23,59,59,999);
                }
                const response = await fetchFunction(userId, null, activeLocation, startTimestamp, endTimestamp);
                if (response && response.matches) {
                    setMatches(response.matches);
                    setFilteredMatches(response.matches);
                    setHasMore(!!response.lastEvaluatedKey && Object.values(response.lastEvaluatedKey).some(v => v));
                    // Store locationString and date range in cache
                    const cacheObj = {
                        matches: response.matches,
                        lastEvaluatedKey: response.lastEvaluatedKey || null,
                        locationString: activeLocation
                    };
                    if (startTimestamp && endTimestamp) {
                        cacheObj.startTimestamp = startTimestamp;
                        cacheObj.endTimestamp = endTimestamp;
                    }
                    localStorage.setItem(cacheKey, JSON.stringify({ data: cacheObj, lastFetched: Date.now() }));
                    console.debug(`[Matches] ${activeMatchTab} lastEvaluatedKey:`, response.lastEvaluatedKey);
                }
                const poolsResponse = await getPools(userId);
                setPools(poolsResponse);
            } catch (err) {
                console.error('Error in fetchData:', err);
                setError(err.message);
                addToast('Error loading data', 'error', 6000);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [isAuthenticated, navigate, addToast, activeMatchTab, activeLocation, appliedDateRange, userId, prefix]);

    const handleLocationSearch = async () => {
        let loc;
        
        // Handle Global selection - construct array of all countries + International
        if (selectedCountry === '__GLOBAL__') {
            const allCountries = locations.map(country => country.name);
            loc = [...allCountries, 'International'];
        } else {
            // Handle single location selection as before
            loc = selectedCountry;
            if (selectedRegion) loc += `#${selectedRegion}`;
            if (selectedSubRegion || selectedLocality) loc += `#${selectedSubRegion}`;
            if (selectedLocality) loc += `#${selectedLocality}`;
        }
        
        // Prepare date range timestamps if set
        let startTimestamp = null, endTimestamp = null;
        if (appliedDateRange && appliedDateRange.startDate && appliedDateRange.endDate) {
            startTimestamp = new Date(appliedDateRange.startDate).setHours(0,0,0,0);
            endTimestamp = new Date(appliedDateRange.endDate).setHours(23,59,59,999);
        }
        
        // For Global selection, we need to compare arrays, so convert activeLocation to array if needed
        let currentActiveLocation = activeLocation;
        if (Array.isArray(loc)) {
            // If we're switching to Global, always trigger a new search
            if (!Array.isArray(currentActiveLocation)) {
                setActiveLocation(loc);
                return;
            }
            // For array comparison, we need to check if arrays are equivalent
            const isSameLocation = Array.isArray(currentActiveLocation) && 
                currentActiveLocation.length === loc.length && 
                currentActiveLocation.every((item, index) => item === loc[index]);
            if (isSameLocation) {
                setLoading(true);
                setHasMore(true);
                localStorage.removeItem(`${prefix}SinglesMatches`);
                localStorage.removeItem(`${prefix}DoublesMatches`);
                setMatches([]);
                try {
                    let response, cacheKey;
                    if (activeMatchTab === 'singles') {
                        response = await getSinglesMatches(userId, null, loc, startTimestamp, endTimestamp);
                        cacheKey = `${prefix}SinglesMatches`;
                    } else {
                        response = await getDoublesMatches(userId, null, loc, startTimestamp, endTimestamp);
                        cacheKey = `${prefix}DoublesMatches`;
                    }
                    if (response && response.matches) {
                        setMatches(response.matches);
                        setFilteredMatches(response.matches);
                        setHasMore(!!response.lastEvaluatedKey && Object.values(response.lastEvaluatedKey).some(v => v));
                        // Store locationString and date range in cache
                        const cacheObj = {
                            matches: response.matches,
                            lastEvaluatedKey: response.lastEvaluatedKey || null,
                            locationString: loc
                        };
                        if (startTimestamp && endTimestamp) {
                            cacheObj.startTimestamp = startTimestamp;
                            cacheObj.endTimestamp = endTimestamp;
                        }
                        localStorage.setItem(cacheKey, JSON.stringify({ data: cacheObj, lastFetched: Date.now() }));
                        console.debug(`[Matches] ${activeMatchTab} lastEvaluatedKey:`, response.lastEvaluatedKey);
                    }
                    const poolsResponse = await getPools(userId);
                    setPools(poolsResponse);
                } catch (err) {
                    console.error('Error in handleLocationSearch:', err);
                    setError(err.message);
                    addToast('Error loading data', 'error', 6000);
                } finally {
                    setLoading(false);
                }
            } else {
                setActiveLocation(loc);
            }
        } else {
            // Handle single location string as before
            if (loc === activeLocation) {
                setLoading(true);
                setHasMore(true);
                localStorage.removeItem(`${prefix}SinglesMatches`);
                localStorage.removeItem(`${prefix}DoublesMatches`);
                setMatches([]);
                try {
                    let response, cacheKey;
                    if (activeMatchTab === 'singles') {
                        response = await getSinglesMatches(userId, null, loc, startTimestamp, endTimestamp);
                        cacheKey = `${prefix}SinglesMatches`;
                    } else {
                        response = await getDoublesMatches(userId, null, loc, startTimestamp, endTimestamp);
                        cacheKey = `${prefix}DoublesMatches`;
                    }
                    if (response && response.matches) {
                        setMatches(response.matches);
                        setFilteredMatches(response.matches);
                        setHasMore(!!response.lastEvaluatedKey && Object.values(response.lastEvaluatedKey).some(v => v));
                        // Store locationString and date range in cache
                        const cacheObj = {
                            matches: response.matches,
                            lastEvaluatedKey: response.lastEvaluatedKey || null,
                            locationString: loc
                        };
                        if (startTimestamp && endTimestamp) {
                            cacheObj.startTimestamp = startTimestamp;
                            cacheObj.endTimestamp = endTimestamp;
                        }
                        localStorage.setItem(cacheKey, JSON.stringify({ data: cacheObj, lastFetched: Date.now() }));
                        console.debug(`[Matches] ${activeMatchTab} lastEvaluatedKey:`, response.lastEvaluatedKey);
                    }
                    const poolsResponse = await getPools(userId);
                    setPools(poolsResponse);
                } catch (err) {
                    console.error('Error in handleLocationSearch:', err);
                    setError(err.message);
                    addToast('Error loading data', 'error', 6000);
                } finally {
                    setLoading(false);
                }
            } else {
                setActiveLocation(loc);
            }
        }
    };

    const loadMoreMatches = useCallback(async () => {
        if (!hasMore || isLoadingMore) return;
        setIsLoadingMore(true);
        try {
            const cacheKey = activeMatchTab === 'singles' 
                ? `${prefix}SinglesMatches` 
                : `${prefix}DoublesMatches`;
            let cached = getCache(cacheKey).data || { matches: [], lastEvaluatedKey: null, startTimestamp: null, endTimestamp: null };
            const cachedMatches = cached?.matches || [];
            const cachedLastKey = cached?.lastEvaluatedKey || null;
            const startTimestamp = cached?.startTimestamp || null;
            const endTimestamp = cached?.endTimestamp || null;
            const fetchFunction = activeMatchTab === 'singles' ? getSinglesMatches : getDoublesMatches;
            console.log("****** Loading More Matches:", cachedLastKey);

            const response = await fetchFunction(userId, cachedLastKey, activeLocation, startTimestamp, endTimestamp);

            console.log("****** Response:", response);

            if (response && response.matches && response.matches.length > 0) {
                const mergedMatches = [...cachedMatches, ...response.matches];
                const updatedLastKey = response.lastEvaluatedKey || null;
                const hasMoreFlag = !!updatedLastKey && Object.values(updatedLastKey).some(v => v);
                setMatches(mergedMatches);
                setFilteredMatches(mergedMatches);
                setHasMore(hasMoreFlag);
                const newCacheObj = {
                    ...cached,
                    matches: mergedMatches,
                    lastEvaluatedKey: updatedLastKey,
                    locationString: activeLocation
                };
                localStorage.setItem(cacheKey, JSON.stringify({ data: newCacheObj, lastFetched: Date.now() }));
                console.debug(`[Matches] ${activeMatchTab} lastEvaluatedKey:`, updatedLastKey);
            } else {
                setHasMore(false);
            }
        } catch (err) {
            console.error('Error loading more matches:', err);
            addToast('Error loading more matches', 'error', 6000);
            setHasMore(false);
        } finally {
            setIsLoadingMore(false);
        }
    }, [activeMatchTab, hasMore, isLoadingMore, addToast, activeLocation, userId, prefix]);

    const applyFilters = useCallback(() => {
        if (!matches.length) {
            setFilteredMatches([]);
            return;
        }

        let filtered = matches;

        if (genderFilter) {
            filtered = filtered.filter(match => {
                const players = [match.winner1, match.winner2, match.opponent1, match.opponent2].filter(Boolean);
                return players.some(player => player.gender === genderFilter);
            });
        }

        if (ageFilter) {
            filtered = filtered.filter(match => {
                const players = [match.winner1, match.winner2, match.opponent1, match.opponent2].filter(Boolean);
                return players.some(player => {
                    const age = parseInt(player.age);
                    switch (ageFilter) {
                        case '<18': return age < 18;
                        case '18-25': return age >= 18 && age <= 25;
                        case '25-35': return age > 25 && age <= 35;
                        case '35-45': return age > 35 && age <= 45;
                        case '45+': return age > 45;
                        default: return true;
                    }
                });
            });
        }

        if (poolFilter && pools.length > 0) {
            const selectedPool = pools.find(p => p.name === poolFilter);
            if (selectedPool && selectedPool.players) {
                filtered = filtered.filter(match => {
                    const players = [match.winner1, match.winner2, match.opponent1, match.opponent2].filter(Boolean);
                    return players.some(player => selectedPool.players.includes(player.userId));
                });
            }
        }

        if (matchTypeFilter) {
            filtered = filtered.filter(match => match.matchType === matchTypeFilter.toLowerCase());
        }

        setFilteredMatches(filtered);
    }, [matches, genderFilter, ageFilter, poolFilter, pools, matchTypeFilter]);

    useEffect(() => {
        applyFilters();
    }, [applyFilters]);

    const clearFilter = (filterType) => {
        switch (filterType) {
            case 'gender':
                setGenderFilter('');
                break;
            case 'pool':
                setPoolFilter('');
                break;
            case 'age':
                setAgeFilter('');
                break;
            case 'type':
                setMatchTypeFilter('');
                break;
            default:
                break;
        }
    };

    const renderMatch = (match, index, isLast) => {
        const matchDate = format(new Date(match.timestamp), 'd MMM yy');
        const players = activeMatchTab === 'singles'
            ? [
                match.winner1 && { ...match.winner1, points: match.winner1Points, change: match.winner1Change, isWinner: true },
                match.opponent1 && { ...match.opponent1, points: match.opponent1Points, change: match.opponent1Change, isWinner: false }
            ].filter(Boolean)
            : [
                match.winner1 && { ...match.winner1, points: match.winner1Points, change: match.winner1Change, isWinner: true },
                match.winner2 && { ...match.winner2, points: match.winner2Points, change: match.winner2Change, isWinner: true },
                match.opponent1 && { ...match.opponent1, points: match.opponent1Points, change: match.opponent1Change, isWinner: false },
                match.opponent2 && { ...match.opponent2, points: match.opponent2Points, change: match.opponent2Change, isWinner: false }
            ].filter(Boolean);

        const involvesUser = [match.winner1, match.winner2, match.opponent1, match.opponent2].some(p => p && p.userId === localUserId);

        return (
            <React.Fragment key={`${match.location}-${match.timestamp}`}>
                {players.map((player, playerIndex) => (
                    <tr 
                        key={`${match.location}-${match.timestamp}-${playerIndex}`}
                        className={index < filteredMatches.length - 1 && playerIndex === players.length - 1 ? 'border-b border-gray-100' : ''}
                    >
                        <td className="py-1 px-2 sm:py-2 sm:px-6">
                            <div className="flex items-center cursor-pointer" onClick={() => setSelectedPlayer(player)}>
                                <div className="relative flex-shrink-0 h-8 w-8 sm:h-10 sm:w-10">
                                    {player.isWinner && (
                                        <div className="absolute inset-0 rounded-full border-2 border-lime-400" style={{ width: '100%', height: '100%', boxSizing: 'border-box' }} />
                                    )}
                                    {player.picture ? (
                                        <img src={player.picture} alt={player.name} className="h-full w-full rounded-full object-cover" />
                                    ) : (
                                        <div className="h-full w-full rounded-full bg-gray-200 flex items-center justify-center">
                                            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                            </svg>
                                        </div>
                                    )}
                                </div>
                                <span className="ml-2 text-sm sm:text-base truncate">{player.name}</span>
                            </div>
                        </td>
                        <td className="py-1 px-2 sm:py-2 sm:px-6 text-left">
                            <span className="text-sm sm:text-base">{player.points}</span>
                            <span className={`ml-1 sm:ml-2 font-semibold text-sm sm:text-base ${player.isWinner ? 'text-green-500' : 'text-red-500'}`}>{player.isWinner ? `+${player.change}` : player.change}</span>
                        </td>
                        {playerIndex === 0 && (
                          <td className="py-1 px-2 sm:py-2 sm:px-6 text-right align-middle" rowSpan={2}>
                            <div className="flex flex-col items-end gap-1 sm:gap-2">
                              <span className="text-xs sm:text-sm text-gray-600">{matchDate}</span>
                              <div className="flex items-center">
                                <span className="font-medium text-sm sm:text-base whitespace-nowrap">{match.score}</span>
                              </div>
                              {/* Reject button - only visible to admin users */}
                              {isAdmin && (
                                <div className="flex gap-2 mt-1">
                                  <button
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      try {
                                        await rejectMatch(localUserId, match.timestamp, activeMatchTab, match.location);
                                        updateCache({ results: null });
                                        removeMatchFromRecentCache(activeMatchTab, match.timestamp);
                                        removeMatchFromUserCache(activeMatchTab, match.timestamp);
                                        removeMatchFromAllCache(activeMatchTab, match.timestamp);
                                        setMatches(prev => prev.filter(m => m.timestamp !== match.timestamp));
                                        setFilteredMatches(prev => prev.filter(m => m.timestamp !== match.timestamp));
                                        updateRankingsCache(activeMatchTab, match.winner1?.userId, -match.winner1Change);
                                        if (activeMatchTab === 'doubles' && match.winner2) {
                                          updateRankingsCache(activeMatchTab, match.winner2?.userId, -match.winner2Change);
                                        }
                                        updateRankingsCache(activeMatchTab, match.opponent1?.userId, -match.opponent1Change);
                                        if (activeMatchTab === 'doubles' && match.opponent2) {
                                          updateRankingsCache(activeMatchTab, match.opponent2?.userId, -match.opponent2Change);
                                        }
                                        const userProfile = JSON.parse(localStorage.getItem('userProfile'))?.data;
                                        const currentUserId = userProfile?.userId;
                                        const pointsChange = 
                                          currentUserId === match.winner1?.userId ? -match.winner1Change :
                                          currentUserId === match.winner2?.userId ? -match.winner2Change :
                                          currentUserId === match.opponent1?.userId ? -match.opponent1Change :
                                          currentUserId === match.opponent2?.userId ? -match.opponent2Change : 0;
                                        
                                        // Determine if user was a winner or loser
                                        const isWinner = currentUserId === match.winner1?.userId || currentUserId === match.winner2?.userId;
                                        const isLoser = currentUserId === match.opponent1?.userId || currentUserId === match.opponent2?.userId;
                                        
                                        // Only update profile if user was involved in the match
                                        if (pointsChange !== 0) {
                                          const updatedProfile = {
                                            ...userProfile,
                                            [activeMatchTab === 'singles' ? 'singlesPoints' : 'doublesPoints']: 
                                              (userProfile[activeMatchTab === 'singles' ? 'singlesPoints' : 'doublesPoints'] || 0) + pointsChange
                                          };
                                          
                                          // Update win/loss stats
                                          if (isWinner) {
                                            const winField = activeMatchTab === 'singles' ? 'singlesWin' : 'doublesWin';
                                            updatedProfile[winField] = Math.max(0, (userProfile[winField] || 0) - 1);
                                          } else if (isLoser) {
                                            const lossField = activeMatchTab === 'singles' ? 'singlesLoss' : 'doublesLoss';
                                            updatedProfile[lossField] = Math.max(0, (userProfile[lossField] || 0) - 1);
                                          }
                                          
                                          localStorage.setItem('userProfile', JSON.stringify({ data: updatedProfile, lastFetched: Date.now() }));
                                        }
                                        addToast('Match rejected successfully', 'success', 6000);
                                      } catch (error) {
                                        console.error('Error rejecting match:', error);
                                        addToast(error.message || 'Failed to reject match', 'error', 6000);
                                      }
                                    }}
                                    className="group relative"
                                  >
                                    <div className="p-1 rounded-full bg-red-100 hover:bg-red-200 transition-colors">
                                      <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                      </svg>
                                    </div>
                                    <span className="absolute bottom-full right-0 mb-2 w-max bg-black text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">Reject</span>
                                  </button>
                                </div>
                              )}
                              {/* Commented out confirmation functionality - matches are now auto-confirmed
                              {involvesUser && !match.confirmed ? (
                                <div className="flex gap-2 mt-1">
                                  {(match.opponent1?.userId === localUserId || match.opponent2?.userId === localUserId) && (
                                    <button
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        try {
                                          await confirmMatch(localUserId, match.timestamp, activeMatchTab, match.location);
                                          markMatchConfirmedInCaches(activeMatchTab, match.timestamp, match.location);
                                          const allCacheKey = activeMatchTab === 'singles' 
                                            ? `${prefix}SinglesMatches` 
                                            : `${prefix}DoublesMatches`;
                                          const allMatchesCache = getCache(allCacheKey).data;
                                          const updatedAllMatches = { 
                                            ...allMatchesCache, 
                                            matches: (allMatchesCache.matches || []).map(m => m.timestamp === match.timestamp ? { ...m, confirmed: true } : m) 
                                          };
                                          setMatches(updatedAllMatches.matches);
                                          setFilteredMatches(updatedAllMatches.matches);
                                          localStorage.setItem(allCacheKey, JSON.stringify({ data: updatedAllMatches, lastFetched: Date.now() }));
                                          addToast('Match confirmed successfully', 'success', 6000);
                                        } catch (error) {
                                          console.error('Error confirming match:', error);
                                          addToast(error.message || 'Failed to confirm match', 'error', 6000);
                                        }
                                      }}
                                      className="group relative"
                                    >
                                      <div className="p-1 rounded-full bg-green-100 hover:bg-green-200 transition-colors">
                                        <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                        </svg>
                                      </div>
                                      <span className="absolute bottom-full right-0 mb-2 w-max bg-black text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">Confirm</span>
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <span className={`${match.confirmed ? 'text-green-600' : 'text-red-600'} text-xs`}>
                                  {match.confirmed ? 'Confirmed' : 'Unconfirmed'}
                                </span>
                              )}
                              */}
                            </div>
                          </td>
                        )}
                    </tr>
                ))}
            </React.Fragment>
        );
    };

    return (
        <div className="p-2 sm:p-6">
            <h3 className="text-xl font-semibold mb-4">{title}</h3>

            <div className="mb-4 flex items-center space-x-1 overflow-x-auto overflow-y-hidden">
                <select value={selectedCountry} onChange={(e) => {
                    setSelectedCountry(e.target.value);
                    setSelectedRegion('');
                    setSelectedSubRegion('');
                    setSelectedLocality('');
                }} className={`bg-white p-1 border rounded pr-4 appearance-none text-sm sm:text-base min-w-[60px] ${selectedCountry ? 'text-black' : 'text-gray-400'}`}>
                    <option value="__GLOBAL__">Global</option>
                    {locations.map(country => <option key={country.name} value={country.name}>{country.name}</option>)}
                </select>

                <select value={selectedRegion} onChange={(e) => {
                    setSelectedRegion(e.target.value);
                    setSelectedSubRegion('');
                    setSelectedLocality('');
                }} className={`bg-white p-1 border rounded pr-4 appearance-none text-sm sm:text-base min-w-[60px] ${selectedRegion ? 'text-black' : 'text-gray-400'}`} disabled={!selectedCountry || selectedCountry === '__GLOBAL__'}>
                    <option value="">Region</option>
                    {locations.find(c => c.name === selectedCountry)?.regions.map(region => <option key={region.name} value={region.name}>{region.name}</option>)}
                </select>

                {(() => {
                    const regionObj = locations.find(c => c.name === selectedCountry)?.regions.find(r => r.name === selectedRegion);
                    if (regionObj && regionObj.subRegions && regionObj.subRegions.length > 0) {
                        return (
                            <select value={selectedSubRegion} onChange={(e) => {
                                setSelectedSubRegion(e.target.value)
                                setSelectedLocality('');
                            }} className={`bg-white p-1 border rounded pr-4 appearance-none text-sm sm:text-base min-w-[60px] ${selectedSubRegion ? 'text-black' : 'text-gray-400'}`} disabled={!selectedRegion}>
                                <option value="">Sub-region</option>
                                {regionObj.subRegions.map(subRegion => <option key={subRegion.name} value={subRegion.name}>{subRegion.name}</option>)}
                            </select>
                        );
                    }
                    return null;
                })()}

                {(() => {
                    const regionObj = locations.find(c => c.name === selectedCountry)?.regions.find(r => r.name === selectedRegion);
                    if (!regionObj) return null;
                    let localities = [];
                    if (regionObj.subRegions && regionObj.subRegions.length > 0 && selectedSubRegion) {
                        const subRegionObj = regionObj.subRegions.find(sr => sr.name === selectedSubRegion);
                        localities = subRegionObj ? subRegionObj.localities : [];
                    } else if (!regionObj.subRegions || regionObj.subRegions.length === 0) {
                        localities = regionObj.localities || [];
                    }
                    return (
                        <select value={selectedLocality} onChange={(e) => setSelectedLocality(e.target.value)} className={`bg-white p-1 border rounded pr-4 appearance-none text-sm sm:text-base min-w-[60px] ${selectedLocality ? 'text-black' : 'text-gray-400'}`} disabled={localities.length === 0}>
                            <option value="">Locality</option>
                            {localities.map(locality => <option key={locality.name} value={locality.name}>{locality.name}</option>)}
                        </select>
                    );
                })()}

                <button
                  type="button"
                  className="bg-white p-1 border rounded appearance-none text-sm sm:text-base flex items-center justify-center"
                  style={{ height: '32px' }}
                  onClick={() => setShowDatePicker(true)}
                  title="Select date range"
                >
                  <FaRegCalendarAlt className="text-xl text-gray-600" />
                </button>

                <button className="p-2 bg-lime-400 text-white rounded hover:bg-lime-500" onClick={handleLocationSearch}>
                    <FaArrowRight />
                </button>
            </div>

            {appliedDateRange && appliedDateRange.startDate && appliedDateRange.endDate && (
              <div className="mt-2 flex flex-wrap gap-2">
                <div className="relative inline-flex items-center bg-white border border-gray-200 rounded px-3 py-1 text-sm text-black font-normal pr-7">
                  <FaRegCalendarAlt className="mr-1 text-gray-500" />
                  <span>
                    {format(appliedDateRange.startDate, 'd MMM yy')} to {format(appliedDateRange.endDate, 'd MMM yy')}
                  </span>
                  <button
                    className="absolute top-1/2 right-2 transform -translate-y-1/2 text-red-500 hover:text-red-700 text-lg font-bold"
                    style={{ padding: 0, background: 'none', border: 'none', lineHeight: 1 }}
                    onClick={() => {
                      setAppliedDateRange(null);
                      setDateRange([{ startDate: null, endDate: null, key: 'selection' }]);
                      const cacheKey = activeMatchTab === 'singles' 
                        ? `${prefix}SinglesMatches` 
                        : `${prefix}DoublesMatches`;
                      const cacheObj = getCache(cacheKey).data;
                      if (cacheObj) {
                        delete cacheObj.startTimestamp;
                        delete cacheObj.endTimestamp;
                        localStorage.setItem(cacheKey, JSON.stringify({ data: cacheObj, lastFetched: Date.now() }));
                      }
                    }}
                    title="Clear"
                  >
                    ×
                  </button>
                </div>
              </div>
            )}

            {showDatePicker && (
              <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-4 shadow-lg">
                  <DateRange
                    editableDateInputs={true}
                    onChange={item => setDateRange([item.selection])}
                    moveRangeOnFirstSelection={false}
                    ranges={dateRange}
                    maxDate={new Date()}
                    showDateDisplay={false}
                    inputRanges={[]}
                    staticRanges={[]}
                  />
                  <div className="flex justify-end mt-2 gap-2">
                    <button
                      className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                      onClick={() => setShowDatePicker(false)}
                    >
                      Cancel
                    </button>
                    <button
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                      onClick={() => {
                        setAppliedDateRange(dateRange[0]);
                        setShowDatePicker(false);
                      }}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="flex border-b border-gray-200 mb-6">
                <button
                    className={`px-4 sm:px-8 py-2 font-medium text-sm focus:outline-none ${
                        activeMatchTab === 'singles'
                            ? 'border-b-2 border-black text-black'
                            : 'text-gray-500 hover:text-black'
                    }`}
                    onClick={() => setActiveMatchTab('singles')}
                >
                    SINGLES
                </button>
                <button
                    className={`px-4 sm:px-8 py-2 font-medium text-sm focus:outline-none ${
                        activeMatchTab === 'doubles'
                            ? 'border-b-2 border-black text-black'
                            : 'text-gray-500 hover:text-black'
                    }`}
                    onClick={() => setActiveMatchTab('doubles')}
                >
                    DOUBLES
                </button>
            </div>

            <div className="mb-4 flex items-center space-x-1 overflow-x-auto overflow-y-hidden">
                <div className="relative">
                    <select
                        value={genderFilter}
                        onChange={(e) => setGenderFilter(e.target.value)}
                        className={`bg-white p-1 border rounded pr-4 appearance-none text-sm sm:text-base min-w-[60px] ${genderFilter === '' ? 'text-gray-400' : 'text-black'}`}
                    >
                        <option value="" disabled hidden>Gender</option>
                        <option value="Male">Men</option>
                        <option value="Female">Ladies</option>
                    </select>
                    {genderFilter !== '' && (
                        <button
                            onClick={() => clearFilter('gender')}
                            className="absolute top-1/2 right-2 transform -translate-y-1/2 translate-x-1 text-red-500 hover:text-red-700 text-lg font-bold"
                            title="Clear"
                        >
                            ×
                        </button>
                    )}
                </div>

                <div className="relative">
                    <button
                        onClick={() => setShowPoolModal(true)}
                        className={`bg-white p-1 border rounded text-left pr-4 appearance-none text-sm sm:text-base min-w-[60px] ${
                            poolFilter ? 'text-black' : 'text-gray-400'
                        }`}
                        style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                        {poolFilter || 'Pool'}
                    </button>
                    {poolFilter && (
                        <button
                            onClick={() => clearFilter('pool')}
                            className="absolute top-1/2 right-2 transform -translate-y-1/2 translate-x-1 text-red-500 hover:text-red-700 text-lg font-bold"
                            title="Clear"
                        >
                            ×
                        </button>
                    )}
                </div>

                <div className="relative">
                    <select
                        value={ageFilter}
                        onChange={(e) => setAgeFilter(e.target.value)}
                        className={`bg-white p-1 border rounded pr-4 appearance-none text-sm sm:text-base min-w-[60px] ${ageFilter === '' ? 'text-gray-400' : 'text-black'}`}
                    >
                        <option value="" disabled hidden>Age group</option>
                        <option value="<18">&lt; 18</option>
                        <option value="18-25">18 - 25</option>
                        <option value="25-35">25 - 35</option>
                        <option value="35-45">35 - 45</option>
                        <option value="45+">45+</option>
                    </select>
                    {ageFilter && (
                        <button
                            onClick={() => clearFilter('age')}
                            className="absolute top-1/2 right-2 transform -translate-y-1/2 translate-x-1 text-red-500 hover:text-red-700 text-lg font-bold"
                            title="Clear"
                        >
                            ×
                        </button>
                    )}
                </div>

                <div className="relative">
                    <select
                        value={matchTypeFilter}
                        onChange={e => setMatchTypeFilter(e.target.value)}
                        className={`bg-white p-1 border rounded pr-4 appearance-none text-sm sm:text-base min-w-[60px] ${matchTypeFilter === '' ? 'text-gray-400' : 'text-black'}`}
                    >
                        <option value="" disabled hidden>Type</option>
                        <option value="Games">Games</option>
                        <option value="Points">Points</option>
                    </select>
                    {matchTypeFilter && (
                        <button
                            onClick={() => clearFilter('type')}
                            className="absolute top-1/2 right-2 transform -translate-y-1/2 translate-x-1 text-red-500 hover:text-red-700 text-lg font-bold"
                            title="Clear"
                        >
                            ×
                        </button>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="overflow-x-auto p-2">
                    <table className="min-w-full">
                        <thead>
                            <tr className="text-xs font-medium text-gray-500 bg-gray-50">
                                <th className="py-1 px-2 sm:py-2 sm:px-6 text-left">PLAYER</th>
                                <th className="py-1 px-2 sm:py-2 sm:px-6 text-left">PTS</th>
                                <th className="py-1 px-2 sm:py-2 sm:px-6 text-right">DETAILS</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="3" className="text-center py-4">
                                        <div className="flex justify-center items-center">
                                            <LoadingSpinner />
                                        </div>
                                    </td>
                                </tr>
                            ) : filteredMatches && filteredMatches.length > 0 ? (
                                filteredMatches
                                    .sort((a, b) => b.timestamp - a.timestamp)
                                    .map((match, index) => renderMatch(
                                        match,
                                        index,
                                        index === filteredMatches.length - 1
                                    ))
                            ) : (
                                <tr>
                                    <td colSpan="3" className="text-center py-4 text-gray-600">
                                        {error ? `Error: ${error}` : 'No matches found'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="flex justify-center mt-4">
                <button
                    onClick={loadMoreMatches}
                    disabled={isLoadingMore || !hasMore}
                    className="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center min-w-[120px]"
                    style={{ minHeight: '40px' }}
                >
                    {isLoadingMore ? (
                        <>
                            <LoadingSpinner className="w-4 h-4 mr-2" />
                            Loading...
                        </>
                    ) : (
                        'Load More'
                    )}
                </button>
            </div>

            <div className="mt-4">
                <AdBlock />
            </div>

            <PoolSelectionModal 
                isOpen={showPoolModal}
                onClose={() => setShowPoolModal(false)}
                pools={pools}
                onSelect={setPoolFilter}
            />

            {selectedPlayer && (
                <ProfileOverlay 
                    player={selectedPlayer}
                    onClose={() => setSelectedPlayer(null)}
                />
            )}
        </div>
    );
};

export default Matches;
